/**
 * Single video card for the Studio Feed (local, no AT Protocol).
 * Displays video player (or thumbnail + link on native), title, and description.
 */

import {useState} from 'react'
import {
  Image,
  Platform,
  Pressable,
  View,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import type {StudioVideo} from '#/lib/api/studio'
import {formatViewCount} from '#/lib/strings/view-count'

export function StudioFeedVideoCard({video}: {video: StudioVideo}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation()
  const [videoError, setVideoError] = useState(false)

  const canPlayInline = Platform.OS === 'web'
  const showPoster = video.thumbnail_path && (videoError || !canPlayInline)
  const onOpenVideo = () => navigation.navigate('StudioVideo', {videoId: video.id})

  return (
    <View
      style={[
        a.p_lg,
        a.border_t,
        t.atoms.border_contrast_low,
        t.atoms.bg,
      ]}>
      {/* Video or thumbnail – clickable to open watch page */}
      <Pressable
        onPress={onOpenVideo}
        style={[
          a.rounded_md,
          t.atoms.bg_contrast_25,
          a.overflow_hidden,
          {aspectRatio: 16 / 9, maxWidth: 600, alignSelf: 'center'},
        ]}
        accessibilityRole="button"
        accessibilityLabel={_(msg`Open video`)}>
        {canPlayInline && !videoError ? (
          <>
            <video
              src={video.video_path}
              poster={video.thumbnail_path ?? undefined}
              controls
              playsInline
              preload="metadata"
              style={{width: '100%', height: '100%', objectFit: 'contain'}}
              onError={() => setVideoError(true)}
            />
            {/* Overlay so clicking thumbnail/poster opens watch page */}
            <Pressable
              style={[
                a.absolute,
                a.inset_0,
                Platform.OS === 'web' && {cursor: 'pointer'},
              ]}
              onPress={onOpenVideo}
            />
          </>
        ) : showPoster ? (
          <View style={[a.flex_1, a.justify_center, a.align_center]}>
            {Platform.OS === 'web' ? (
              <img
                src={video.thumbnail_path!}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
              />
            ) : (
              <Image
                source={{uri: video.thumbnail_path!}}
                style={[a.flex_1, {width: '100%'}]}
                resizeMode="contain"
              />
            )}
            {Platform.OS !== 'web' && (
              <View
                style={[
                  a.absolute,
                  a.inset_0,
                  a.justify_center,
                  a.align_center,
                  t.atoms.bg_contrast_50,
                ]}>
                <Button
                  size="small"
                  color="primary"
                  label={_(msg`Watch`)}
                  onPress={onOpenVideo}>
                  <ButtonText>
                    <Trans>Watch</Trans>
                  </ButtonText>
                </Button>
              </View>
            )}
          </View>
        ) : (
          <View style={[a.flex_1, a.justify_center, a.align_center]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>Video</Trans>
            </Text>
            {Platform.OS !== 'web' && (
              <Button
                size="small"
                color="primary"
                label={_(msg`Open`)}
                onPress={onOpenVideo}>
                <ButtonText>
                  <Trans>Open</Trans>
                </ButtonText>
              </Button>
            )}
          </View>
        )}
      </Pressable>
      <View style={[a.pt_sm]}>
        <Text
          style={[a.text_lg, a.font_semi_bold, t.atoms.text]}
          numberOfLines={2}>
          {video.title || _(msg`Untitled`)}
        </Text>
        {video.description ? (
          <Text
            style={[a.text_sm, a.pt_xs, t.atoms.text_contrast_medium]}
            numberOfLines={4}>
            {video.description}
          </Text>
        ) : null}
        <Text
          style={[a.text_xs, a.pt_xs, t.atoms.text_contrast_low]}
          numberOfLines={1}>
          {formatViewCount(video.total_views)} <Trans>views</Trans>
        </Text>
      </View>
    </View>
  )
}
